import { useMemo } from 'react';
import type { RawAxiosRequestConfig } from 'axios';

import type {
  CreateUsersPasswordResetRequest,
  CreateUsersUsersRequestDataAttributesProfile,
  ListUsersUsersFilterRolesEnum,
  ListUsersUsersFilterSegmentationTypesEnum,
  UsersApiDestroyUsersUsersIdRequest,
  UsersApiListUsersUsersRequest,
  UsersApiRetrieveUsersUsersIdRequest,
} from '@spazious/client-backoffice';
import { keepPreviousData, type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';

import { normalizeApiResponse } from '../_normalize.js';
import { useSwallowedMutation } from '../_useSwallowedMutation.js';
import {
  invalidateMutationKey,
  joinCsv,
  joinInclude,
  retryServerErrorsOnly,
  stableFilterValues,
} from '../_utils.js';
import { useApiClient } from '../useApiClient.js';

export const USERS_QUERY_KEY = 'users';

export type UsersFilters = {
  hotelChainSlug?: string;
  hotelChainShortUuid?: string;
  hotelShortUuid?: string;
  profileHotelChainSlug?: string;
  profileHotelChainShortUuid?: string;
  profileHotelShortUuid?: string;
  lastLoginGt?: string;
  lastLoginLt?: string;
  lastLoginGte?: string;
  lastLoginLte?: string;
  clusters?: string | string[];
  hotels?: string | string[];
  hotelChain?: number | string;
  segmentationTypes?: string | ListUsersUsersFilterSegmentationTypesEnum[];
  roles?: string | ListUsersUsersFilterRolesEnum[];
  profileMarkets?: string | string[];
  status?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  countries?: string | string[];
  search?: string;
};

export type UsersListVariables = {
  pageNumber?: number;
  pageSize?: number;
  sort?: string;
  include?: string[];
  fields?: Record<string, string>;
  filters?: UsersFilters;
};

export type CopyScopesVariables = {
  sourceUserId: number;
  targetUserIds: number[];
};

export type CreateUserVariables = {
  firstName?: string;
  email: string;
  lastName?: string;
  isActive?: boolean;
  isApiUser?: boolean;
  profile: CreateUsersUsersRequestDataAttributesProfile;
};

// `isApiUser` is create-only: the backend sets it on creation and silently ignores
// it on PATCH (users.UserUpdateSerializer has no such field).
export type UpdateUserVariables = Omit<CreateUserVariables, 'isApiUser'> & {
  userId: number | string;
};

export type PasswordResetVariables = {
  email: string;
  requestOptions?: RawAxiosRequestConfig;
};

type UseUsersParams = {
  userId?: number;
  variables?: UsersListVariables;
  retrieveInclude?: string[];
  retrieveFields?: Record<string, string>;
  enabled?: boolean;
  // Cache key(s) to invalidate after any of this hook's mutations. Consumers
  // declare which caches the mutation affects — pass `[USERS_QUERY_KEY]` for
  // the resource itself, plus `[ME_QUERY_KEY]` when the action can be a
  // self-edit (e.g. `updateUser` of the current user). Omit for read-only
  // usage.
  mutationKey?: QueryKey | QueryKey[];
  onCreate?: () => void;
  onUpdate?: () => void;
  onDelete?: () => void;
  onDeleteBulk?: () => void;
  onCopyScopes?: () => void;
  onResetScopes?: () => void;
  onPasswordReset?: () => void;
  onError?: (error: unknown) => void;
};

export const useUsers = ({
  userId,
  variables,
  retrieveInclude,
  retrieveFields,
  enabled = true,
  mutationKey,
  onCreate,
  onUpdate,
  onDelete,
  onDeleteBulk,
  onCopyScopes,
  onResetScopes,
  onPasswordReset,
  onError,
}: UseUsersParams = {}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { pageNumber, pageSize, sort, include, fields, filters } = variables ?? {};

  const listKey = useMemo(
    () => [
      USERS_QUERY_KEY,
      'list',
      { pageNumber, pageSize, sort, include: include?.join(','), fields },
      ...stableFilterValues(filters as Record<string, unknown> | undefined),
    ],
    [pageNumber, pageSize, sort, include, fields, filters]
  );

  const retrieveKey = useMemo(
    () => [USERS_QUERY_KEY, 'retrieve', userId, retrieveInclude?.join(','), retrieveFields],
    [userId, retrieveInclude, retrieveFields]
  );

  const {
    data: usersData,
    isFetching: usersFetching,
    isError: usersError,
    error: usersErrorDetail,
  } = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const params: UsersApiListUsersUsersRequest = {
        pageNumber,
        pageSize,
        sort,
        include: joinInclude(include),
        fields,
        filterClusters: joinCsv(filters?.clusters),
        filterCountries: joinCsv(filters?.countries),
        filterEmail: filters?.email,
        filterFirstName: filters?.firstName,
        filterHotels: joinCsv(filters?.hotels),
        filterHotelChain:
          filters?.hotelChain !== undefined ? String(filters.hotelChain) : undefined,
        filterLastLoginGt: filters?.lastLoginGt,
        filterLastLoginGte: filters?.lastLoginGte,
        filterLastLoginLt: filters?.lastLoginLt,
        filterLastLoginLte: filters?.lastLoginLte,
        filterLastName: filters?.lastName,
        filterProfileHotelChainSlug: filters?.profileHotelChainSlug ?? filters?.hotelChainSlug,
        filterProfileHotelChainShortUuid:
          filters?.profileHotelChainShortUuid ?? filters?.hotelChainShortUuid,
        filterProfileHotelShortUuid: filters?.profileHotelShortUuid ?? filters?.hotelShortUuid,
        filterProfileMarkets: joinCsv(filters?.profileMarkets),
        filterRoles: joinCsv(filters?.roles) as ListUsersUsersFilterRolesEnum | undefined,
        filterSearch: filters?.search,
        filterSegmentationTypes: joinCsv(filters?.segmentationTypes) as
          | ListUsersUsersFilterSegmentationTypesEnum
          | undefined,
        filterStatus: filters?.status,
      };
      const response = await apiClient.users.listUsersUsers(params);
      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: !userId && enabled,
    retry: retryServerErrorsOnly,
  });

  const { data: userData, isFetching: userFetching } = useQuery({
    queryKey: retrieveKey,
    queryFn: async () => {
      const params: UsersApiRetrieveUsersUsersIdRequest = {
        id: String(userId),
        include: joinInclude(retrieveInclude),
        fields: retrieveFields,
      };
      const response = await apiClient.users.retrieveUsersUsersId(params);
      return response.data;
    },
    enabled: !!userId && enabled,
  });

  const normalizedUsersData = useMemo(
    () => (usersData ? normalizeApiResponse(usersData) : []),
    [usersData]
  );

  const normalizedUserData = useMemo(
    () => (userData ? normalizeApiResponse(userData) : null),
    [userData]
  );

  const invalidate = () => invalidateMutationKey(queryClient, mutationKey);

  const { mutateAsync: createUser, isPending: createUserPending } = useSwallowedMutation({
    mutationFn: async ({
      firstName,
      lastName,
      email,
      isActive,
      isApiUser,
      profile,
    }: CreateUserVariables) => {
      const response = await apiClient.users.createUsersUsers({
        createUsersUsersRequest: {
          data: {
            type: 'User',
            attributes: { firstName, lastName, email, isActive, isApiUser, profile },
          },
        },
      });
      return response.data;
    },
    onSuccess: () => {
      onCreate?.();
      invalidate();
    },
    onError,
  });

  const { mutateAsync: updateUser, isPending: updateUserPending } = useSwallowedMutation({
    mutationFn: async ({
      userId: id,
      firstName,
      lastName,
      email,
      isActive,
      profile,
    }: UpdateUserVariables) => {
      const stringId = String(id);
      const response = await apiClient.users.partialUpdateUsersUsersId({
        id: stringId,
        partialUpdateUsersUsersIdRequest: {
          data: {
            id: stringId,
            type: 'User',
            attributes: { firstName, lastName, email, isActive, profile },
          },
        },
      });
      return response.data;
    },
    onSuccess: () => {
      onUpdate?.();
      invalidate();
    },
    onError,
  });

  const { mutateAsync: deleteUser, isPending: deleteUserPending } = useSwallowedMutation({
    mutationFn: async (data: UsersApiDestroyUsersUsersIdRequest) => {
      const response = await apiClient.users.destroyUsersUsersId(data);
      return response.data;
    },
    onSuccess: () => {
      onDelete?.();
      invalidate();
    },
    onError,
  });

  const { mutateAsync: deleteBulkUsers, isPending: deleteBulkPending } = useSwallowedMutation({
    mutationFn: async ({ userIds }: { userIds: Array<number | string> }) => {
      const response = await apiClient.users.bulkDeleteUsersUsersBulkDelete({
        // The generated client types this endpoint with no body; the backend
        // expects { data: { ids: [...] } } passed through axios request config.
        data: { ids: userIds },
      } as unknown as RawAxiosRequestConfig);
      return response.data;
    },
    onSuccess: () => {
      onDeleteBulk?.();
      invalidate();
    },
    onError,
  });

  const { mutateAsync: copyScopes, isPending: copyScopesPending } = useSwallowedMutation({
    mutationFn: async ({ sourceUserId, targetUserIds }: CopyScopesVariables) => {
      const response = await apiClient.users.copyScopesUsersUsersCopyScopes({
        userCopyScopes: {
          source_user_id: sourceUserId,
          target_user_ids: targetUserIds,
        } as never,
      });
      return response.data;
    },
    onSuccess: () => {
      onCopyScopes?.();
      invalidate();
    },
    onError,
  });

  const { mutateAsync: resetScopes, isPending: resetScopesPending } = useSwallowedMutation({
    mutationFn: async ({ usersId }: { usersId: number[] }) => {
      const response = await apiClient.users.resetScopesUsersUsersBulkResetScopes({
        userResetScopes: { ids: usersId } as never,
      });
      return response.data;
    },
    onSuccess: () => {
      onResetScopes?.();
      invalidate();
    },
    onError,
  });

  const { mutateAsync: passwordReset, isPending: passwordResetPending } = useSwallowedMutation({
    mutationFn: async ({ email, requestOptions }: PasswordResetVariables) => {
      const options: RawAxiosRequestConfig = {
        ...(requestOptions ?? {}),
        headers: {
          'Content-Type': 'application/json',
          ...((requestOptions?.headers as Record<string, string> | undefined) ?? {}),
        },
      };
      // Boundary cast: the OpenAPI spec wrongly declares a JSON:API envelope for this
      // endpoint. The backend view (django-rest-passwordreset) parses flat JSON, which
      // is exactly what this sends (with the application/json override above) and what
      // production has always sent.
      const response = await apiClient.users.createUsersPasswordReset(
        {
          createUsersPasswordResetRequest: {
            email,
          } as unknown as CreateUsersPasswordResetRequest,
        },
        options
      );
      return response.data;
    },
    onSuccess: () => {
      onPasswordReset?.();
    },
    onError,
  });

  return useMemo(
    () => ({
      usersData,
      normalizedUsersData,
      userData,
      normalizedUserData,
      isFetching: usersFetching || userFetching,
      isError: usersError,
      error: usersErrorDetail,
      isPending:
        createUserPending ||
        updateUserPending ||
        deleteUserPending ||
        deleteBulkPending ||
        copyScopesPending ||
        resetScopesPending ||
        passwordResetPending,
      createUser,
      updateUser,
      deleteUser,
      deleteBulkUsers,
      copyScopes,
      resetScopes,
      passwordReset,
    }),
    [
      usersData,
      normalizedUsersData,
      userData,
      normalizedUserData,
      usersFetching,
      userFetching,
      usersError,
      usersErrorDetail,
      createUserPending,
      updateUserPending,
      deleteUserPending,
      deleteBulkPending,
      copyScopesPending,
      resetScopesPending,
      passwordResetPending,
      createUser,
      updateUser,
      deleteUser,
      deleteBulkUsers,
      copyScopes,
      resetScopes,
      passwordReset,
    ]
  );
};
